import type { UserSettings } from "@/types/user-settings";

/** Keys in `UserSettings` that belong to the project display slice (DB-backed when forced). */
export const PROJECT_DISPLAY_SETTING_KEYS = [
  "windUnit",
  "windGustsUnit",
  "temperatureUnit",
  "precipitationUnit",
  "windWavesUnit",
  "snowUnit",
  "altitudeUnit",
  "visibilityUnit",
  "timezone",
  "locationCoordinateDisplays",
] as const satisfies ReadonlyArray<keyof UserSettings>;

export type ProjectDisplaySettingKey = (typeof PROJECT_DISPLAY_SETTING_KEYS)[number];

export function isProjectDisplaySettingKey(
  key: keyof UserSettings,
): key is ProjectDisplaySettingKey {
  return (PROJECT_DISPLAY_SETTING_KEYS as readonly string[]).includes(key);
}

const WIND = ["m/s", "km/h", "mph", "kt", "bft"] as const;
const WIND_GUSTS = ["m/s", "km/h", "mph", "kt"] as const;
const TEMP = ["°C", "°F", "K"] as const;
const PRECIP = ["mm/hr", "inch/hr"] as const;
const WAVES = ["m", "ft"] as const;
const SNOW = ["cm", "inch"] as const;
const ALT = ["m", "ft"] as const;
const VIS = ["m", "ft", "km", "mi"] as const;
const TZ = ["local", "utc", "at_location"] as const;

function pick<C extends readonly string[]>(v: string, allowed: C, fallback: C[number]): C[number] {
  return (allowed as readonly string[]).includes(v) ? (v as C[number]) : fallback;
}

/** Row shape from DB / API JSON (no Prisma import required here). */
export type ProjectSettingsLike = {
  windUnit: string;
  windGustsUnit: string;
  temperatureUnit: string;
  precipitationUnit: string;
  windWavesUnit: string;
  snowUnit: string;
  altitudeUnit: string;
  visibilityUnit: string;
  timezone: string;
  coordDecimal: boolean;
  coordDms: boolean;
  coordUtm: boolean;
};

export type ProjectDisplayUserSlice = ReturnType<typeof projectSettingsLikeToUserSlice>;

export function projectSettingsLikeToUserSlice(
  row: ProjectSettingsLike,
): Pick<
  UserSettings,
  | "windUnit"
  | "windGustsUnit"
  | "temperatureUnit"
  | "precipitationUnit"
  | "windWavesUnit"
  | "snowUnit"
  | "altitudeUnit"
  | "visibilityUnit"
  | "timezone"
  | "locationCoordinateDisplays"
> {
  return {
    windUnit: pick(row.windUnit, WIND, "bft"),
    windGustsUnit: pick(row.windGustsUnit, WIND_GUSTS, "km/h"),
    temperatureUnit: pick(row.temperatureUnit, TEMP, "°C"),
    precipitationUnit: pick(row.precipitationUnit, PRECIP, "mm/hr"),
    windWavesUnit: pick(row.windWavesUnit, WAVES, "m"),
    snowUnit: pick(row.snowUnit, SNOW, "cm"),
    altitudeUnit: pick(row.altitudeUnit, ALT, "m"),
    visibilityUnit: pick(row.visibilityUnit, VIS, "m"),
    timezone: pick(row.timezone, TZ, "local"),
    locationCoordinateDisplays: {
      decimal: row.coordDecimal,
      dms: row.coordDms,
      utm: row.coordUtm,
    },
  };
}
